/*
 * Bennett Bitters — automatic cart gift
 * Adds a free bottle of lavender bitters when the qualifying cart subtotal
 * reaches the threshold, and removes it when the cart drops back below.
 *
 * SETUP
 * 1. Create the gift product in Shopify:
 *      - Title: "Lavender Bitters — Gift"
 *      - Price: 0.00
 *      - Published to Online Store, but NOT in any collection
 *      - Uncheck "track quantity" (or keep stock, see OUT OF STOCK below)
 * 2. GIFT_VARIANT_ID below is already set to the live variant. If you ever
 *    recreate the gift product, get the new variant id by opening
 *      https://YOURSTORE.com/products/lavender-bitters-gift.js
 *    and reading variants[0].id — note that is NOT the same as the product id
 *    shown at the top of that JSON.
 * 3. Upload this file as assets/bennett-gift-cart.js, then add to theme.liquid
 *    inside <head>, immediately after {{ content_for_header }}:
 *      <script src="{{ 'bennett-gift-cart.js' | asset_url }}"></script>
 *    Load it early and WITHOUT defer. The script patches window.fetch to
 *    detect cart writes; if the theme's cart code captures its own reference
 *    to fetch before this runs, those writes go undetected.
 */

(function () {
  'use strict';

  var CONFIG = {
    // ---- REQUIRED ----
    // Variant id for "Lavender Bitters — Gift" (product 10503082967315).
    // Confirmed from /products/lavender-bitters-gift.js on 2026-08-16.
    GIFT_VARIANT_ID: 54575254962451,

    // ---- TUNING ----
    THRESHOLD_CENTS: 99999999,            // $50.00
    GIFT_PROPERTY: '__gift',          // line item property used to tag the gift
    ALLOW_DISMISS: true,              // let a customer remove the gift and keep it gone
    DISMISS_KEY: 'bb_gift_dismissed', // sessionStorage key for the above
    SYNC_DELAY_MS: 300,               // wait for the theme's own render before touching the cart
    DEBUG: false
  };

  // ---------------------------------------------------------------- helpers

  function log() {
    if (CONFIG.DEBUG) console.log.apply(console, ['[gift]'].concat([].slice.call(arguments)));
  }

  // `internal` guards our own cart writes so they don't retrigger sync()
  var internal = false;
  var busy = false;
  var sawGiftLastRun = false;

  function isGiftLine(line) {
    if (line.variant_id === CONFIG.GIFT_VARIANT_ID) return true;
    return !!(line.properties && line.properties[CONFIG.GIFT_PROPERTY]);
  }

  function dismissed() {
    if (!CONFIG.ALLOW_DISMISS) return false;
    try { return sessionStorage.getItem(CONFIG.DISMISS_KEY) === '1'; } catch (e) { return false; }
  }

  function setDismissed(v) {
    try {
      if (v) sessionStorage.setItem(CONFIG.DISMISS_KEY, '1');
      else sessionStorage.removeItem(CONFIG.DISMISS_KEY);
    } catch (e) { /* private mode — fail open */ }
  }

  function json(url, body) {
    var opts = {
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin'
    };
    if (body) { opts.method = 'POST'; opts.body = JSON.stringify(body); }
    return nativeFetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  // ------------------------------------------------------------ cart writes

  function getCart() {
    return json('/cart.js');
  }

  // If the configured id is a PRODUCT id rather than a VARIANT id, the add
  // below is rejected. Rather than fail silently, look the product up once in
  // the storefront feed and swap in its first variant.
  var resolveAttempted = false;

  function resolveVariantId() {
    if (resolveAttempted) return Promise.resolve(false);
    resolveAttempted = true;

    var found = null;

    return [1, 2, 3].reduce(function (chain, page) {
      return chain.then(function () {
        if (found) return;
        return json('/products.json?limit=250&page=' + page).then(function (data) {
          (data.products || []).forEach(function (p) {
            if (p.id === CONFIG.GIFT_VARIANT_ID && p.variants && p.variants.length) {
              found = p.variants[0].id;
            }
          });
        }).catch(function () { /* feed disabled or empty page */ });
      });
    }, Promise.resolve()).then(function () {
      if (found) {
        log('resolved product id to variant id', found);
        CONFIG.GIFT_VARIANT_ID = found;
        return true;
      }
      console.warn('[gift] Could not resolve ' + CONFIG.GIFT_VARIANT_ID +
        ' to a variant. Set GIFT_VARIANT_ID to the variant id — see SETUP.');
      return false;
    });
  }

  function addGift() {
    return attemptAdd().then(function (ok) {
      if (ok) return true;
      return resolveVariantId().then(function (swapped) {
        return swapped ? attemptAdd() : false;
      });
    });
  }

  function attemptAdd() {
    internal = true;
    return json('/cart/add.js', {
      items: [{
        id: CONFIG.GIFT_VARIANT_ID,
        quantity: 1,
        properties: (function () { var p = {}; p[CONFIG.GIFT_PROPERTY] = 'true'; return p; })()
      }]
    }).then(function () {
      log('gift added');
      return true;
    }).catch(function (e) {
      // Most likely cause: gift variant is out of stock. Fail silently —
      // the customer just doesn't get a gift rather than seeing a broken cart.
      log('add failed', e);
      return false;
    }).finally(function () { internal = false; });
  }

  function setLineQty(key, qty) {
    internal = true;
    return json('/cart/change.js', { id: key, quantity: qty })
      .then(function () { return true; })
      .catch(function (e) { log('change failed', e); return false; })
      .finally(function () { internal = false; });
  }

  // --------------------------------------------------------------- the sync

  function qualifyingSubtotal(cart) {
    // Gift lines never count toward the threshold, so the gift can't pay for itself.
    return cart.items.reduce(function (sum, line) {
      return isGiftLine(line) ? sum : sum + line.final_line_price;
    }, 0);
  }

  function sync() {
    if (busy) return Promise.resolve();
    busy = true;

    return getCart().then(function (cart) {
      var giftLines = cart.items.filter(isGiftLine);
      var qualifies = qualifyingSubtotal(cart) >= CONFIG.THRESHOLD_CENTS;
      var chain = Promise.resolve(false);

      log('subtotal', qualifyingSubtotal(cart), 'qualifies', qualifies, 'giftLines', giftLines.length);

      if (!qualifies) {
        // Below threshold: strip every gift line and reset the dismissal,
        // so the offer is fresh if they build the cart back up.
        giftLines.forEach(function (line) {
          chain = chain.then(function () { return setLineQty(line.key, 0); });
        });
        if (giftLines.length) setDismissed(false);

      } else if (giftLines.length === 0) {
        // Qualifies but no gift. If we saw one a moment ago, the customer
        // removed it themselves — respect that instead of fighting them.
        if (sawGiftLastRun && CONFIG.ALLOW_DISMISS) {
          setDismissed(true);
          log('customer removed gift — not re-adding');
        } else if (!dismissed()) {
          chain = chain.then(addGift);
        }

      } else {
        // Qualifies and gift present. Collapse duplicates and clamp to one.
        giftLines.slice(1).forEach(function (line) {
          chain = chain.then(function () { return setLineQty(line.key, 0); });
        });
        if (giftLines[0].quantity !== 1) {
          chain = chain.then(function () { return setLineQty(giftLines[0].key, 1); });
        }
      }

      return chain.then(function (changed) {
        sawGiftLastRun = qualifies && giftLines.length > 0;
        if (changed) refreshCartUI();
      });
    }).catch(function (e) {
      log('sync failed', e);
    }).finally(function () {
      busy = false;
    });
  }

  // ----------------------------------------------------------- UI refresh
  // This is the one theme-dependent piece. The event below is enough for
  // most custom drawers; the reload is a blunt fallback for the cart page.

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('bb:gift-cart-updated', { bubbles: true }));

    var onCartPage = window.location.pathname.replace(/\/$/, '').endsWith('/cart');
    var jq = window.jQuery || window.$;

    // Impulse / Archetype themes rebuild the drawer when a quantity input
    // reports a change. It's the only redraw hook the theme exposes without
    // editing theme.js. The resulting /cart/change.js call re-enters sync(),
    // which finds nothing to fix and stops — so this doesn't loop.
    if (!onCartPage && jq && jq('form.ajaxcart .js-qty__num').length) {
      log('nudging Impulse drawer');
      jq('form.ajaxcart .js-qty__num').first().trigger('change');
      return;
    }

    // Generic fallbacks for other themes.
    document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    // The cart page renders line items server-side, so nothing short of a
    // re-render will show the change.
    if (onCartPage) window.location.reload();
  }

  // ------------------------------------------------- listen for cart writes
  // Themes mutate the cart in a dozen different ways, so rather than hooking
  // a specific theme's events we watch the network for cart endpoints.

  var nativeFetch = window.fetch.bind(window);
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = nativeFetch(input, init);
    if (!internal && CART_WRITE.test(url)) {
      promise.then(function () { setTimeout(sync, CONFIG.SYNC_DELAY_MS); }).catch(function () {});
    }
    return promise;
  };

  // Older themes still use XHR for the ajax cart.
  var nativeOpen = XMLHttpRequest.prototype.open;
  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__bbCartWrite = CART_WRITE.test(url || '');
    return nativeOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__bbCartWrite && !internal) {
      this.addEventListener('load', function () { setTimeout(sync, CONFIG.SYNC_DELAY_MS); });
    }
    return nativeSend.apply(this, arguments);
  };

  // ------------------------------------------------------------------ boot

  function boot() {
    if (!CONFIG.GIFT_VARIANT_ID || String(CONFIG.GIFT_VARIANT_ID).length < 10) {
      console.warn('[gift] GIFT_VARIANT_ID is not set — script idle.');
      return;
    }
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Non-ajax add-to-cart forms cause a full navigation, and bfcache restores
  // skip DOMContentLoaded, so re-check on show.
  window.addEventListener('pageshow', function (e) { if (e.persisted) sync(); });

  window.BennettGift = { sync: sync, config: CONFIG };
})();